import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";

interface SessionExpiredModalProps {
  isOpen: boolean;
  onClose?: () => void;
  message?: string;
}

/**
 * Modal that notifies users when their authentication session has expired.
 * Provides a clear call-to-action to log back in.
 */
export function SessionExpiredModal({
  isOpen,
  onClose,
  message = "Your session has expired. Please log in again to continue.",
}: SessionExpiredModalProps) {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);

  if (!isOpen) return null;

  const handleLogin = () => {
    // Clear the auth state
    logout();
    
    // Close modal if callback provided
    onClose?.();
    
    // Navigate to login page
    navigate("/login", { 
      state: { 
        returnUrl: window.location.pathname + window.location.search,
        sessionExpired: true 
      } 
    });
  };

  const handleClose = () => {
    // Clear the auth state
    logout();
    
    // Close modal if callback provided
    onClose?.();
    
    // Navigate to login page (same as handleLogin for consistency)
    navigate("/login", { 
      state: { 
        returnUrl: window.location.pathname + window.location.search,
        sessionExpired: true 
      } 
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - slightly less transparent to emphasize importance */}
      <button
        type="button"
        aria-label="Close modal"
        onClick={handleClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md bg-white dark:bg-surface-900 border border-orange-500/30 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header with warning color */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-surface-700/50 bg-orange-50 dark:bg-orange-500/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-orange-500 dark:text-orange-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-surface-100">
                Session Expired
              </h2>
              <p className="text-sm text-gray-600 dark:text-surface-400">
                Your login session has timed out
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 bg-white dark:bg-surface-900">
          <p className="text-gray-700 dark:text-surface-300 text-sm leading-relaxed">
            {message}
          </p>
          
          <div className="mt-4 p-3 bg-gray-50 dark:bg-surface-800/50 rounded-lg border border-gray-200 dark:border-surface-700/50">
            <div className="flex items-start gap-2">
              <svg
                className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-gray-600 dark:text-surface-400 leading-relaxed">
                For your security, you're automatically logged out after 24 hours of inactivity. 
                Your data is safe and you can log back in at any time.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-surface-800/50 border-t border-gray-200 dark:border-surface-700/50 flex gap-3">
          <button
            type="button"
            onClick={handleLogin}
            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-colors shadow-lg shadow-purple-500/20"
          >
            Log In Again
          </button>
        </div>
      </div>
    </div>
  );
}
